/**
 * O socorro das fotos: nenhuma imagem do sistema morre por causa do cookie.
 *
 * O caminho normal de uma foto é `<img src="/api/fotos/...">` com o cookie de
 * sessão — rápido e com cache do navegador. Mas esse caminho tem um elo que o
 * resto da API não tem (o cookie), e quando ele falha, falha EM SILÊNCIO: o
 * `<img>` só dispara `error`, sem dizer por quê. Foi o "as fotos vivem caindo"
 * de 04/08.
 *
 * Este módulo é a rede embaixo do trapézio: um ouvinte ÚNICO, no documento,
 * pega o `error` de qualquer `<img>` de foto nossa — de toda tela que existe e
 * de toda tela que ainda vai existir, sem ninguém precisar lembrar de nada — e
 * tenta de novo pelo canal autenticado (`api.fotoAutenticada`), que não usa
 * cookie e por isso não cai junto.
 *
 * Uma tentativa só, por imagem: se o socorro também falhar, o `onerror` da
 * própria tela (quando houver) mostra o aviso dela. Sem essa trava, imagem
 * quebrada de verdade viraria laço infinito de tentativas.
 *
 * O mesmo vale para LINK de arquivo nosso — ver `abrirLinkDeArquivo`.
 */

import { api, ErroApi } from '../api.js';

/**
 * Link para arquivo nosso (o anexo da caixa de entrada, o PDF da nota de
 * oficina) tem o mesmo elo do cookie, e quando ele falha é pior que na imagem:
 * a aba nova é uma navegação comum, longe de qualquer socorro, e a pessoa dá
 * de cara com o JSON cru do 401 — "Faça login para continuar." Foi o anexo da
 * caixa de entrada em 29/10, com o cookie sem abrir desde pelo menos 29/09
 * (a conta do Cloud Run não tem permissão de criar a sessão no Firebase), e
 * ninguém tinha notado porque as imagens eram socorridas aqui embaixo.
 *
 * Por isso o clique nem tenta o cookie: abre a aba NA HORA, ainda dentro do
 * clique (depois de um `await` o navegador trata como pop-up e bloqueia),
 * baixa pelo canal autenticado e leva a aba até o arquivo.
 */
function abrirLinkDeArquivo(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0) return;
  const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return;
  if (!new URL(link.href).pathname.startsWith('/api/fotos/')) return;
  e.preventDefault();

  const aba = window.open('', '_blank');
  if (aba) {
    // A aba não precisa alcançar o sistema de volta; só ela é que é guiada.
    aba.opener = null;
    aba.document.title = 'Abrindo o arquivo…';
    aba.document.body.style.font = '16px system-ui, sans-serif';
    aba.document.body.textContent = 'Abrindo o arquivo…';
  }
  api
    .fotoAutenticada(link.href)
    .then((local) => {
      if (aba) aba.location.href = local;
      else window.open(local, '_blank');
    })
    .catch((erro) => {
      const motivo =
        erro instanceof ErroApi && erro.status === 403
          ? 'Seu acesso não abre este documento.'
          : erro instanceof ErroApi && erro.status === 404
            ? 'O arquivo não está mais no armazenamento.'
            : 'Tente de novo em instantes.';
      if (!aba) return;
      aba.document.title = 'O arquivo não abriu';
      aba.document.body.textContent = `Não consegui abrir o arquivo. ${motivo}`;
    });
}

export function instalarSocorroDeFotos(): void {
  // Fase normal (de borbulha), não de captura: se a tela tratar o clique e
  // cancelar, `defaultPrevented` chega aqui e o link fica com ela.
  document.addEventListener('click', abrirLinkDeArquivo);

  document.addEventListener(
    'error',
    (e) => {
      const img = e.target;
      if (!(img instanceof HTMLImageElement)) return;
      const alvo = img.src;
      if (!alvo || alvo.startsWith('data:') || alvo.startsWith('blob:')) return;
      if (!alvo.includes('/api/fotos/')) return;

      if (img.dataset['socorrida']) return; // segunda queda: a tela decide
      img.dataset['socorrida'] = '1';

      // Segura o `onerror` da tela NESTA queda: o socorro ainda vai tentar.
      e.stopPropagation();

      api
        .fotoAutenticada(alvo)
        .then((local) => {
          img.src = local;
        })
        .catch(() => {
          // Repõe a mesma src: o novo `error` passa reto por aqui (a marca
          // `socorrida` já está) e chega ao `onerror` da tela, que mostra o
          // aviso dela — "sem foto", "a foto não abriu", o que for.
          img.src = alvo;
        });
    },
    // Fase de captura: `error` não borbulha, então um ouvinte comum no
    // documento nunca o veria. Na captura, ele passa por aqui ANTES de chegar
    // ao `onerror` da própria imagem — é o que permite segurá-lo.
    true,
  );
}
